import React from 'react';
import { useQuery } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import providerService from '@/services/providerService';

interface ProviderDetailContentProps {
  id: string;
}

const ProviderDetailContent: React.FC<ProviderDetailContentProps> = ({ id }) => {
  const { data: provider, isLoading, error } = useQuery({
    queryKey: ['provider', id],
    queryFn: () => providerService.getProviderDetail(id),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-center">
        <MaterialIcon icon="error_outline" className="text-4xl text-gray-400" />
        <p className="text-gray-500">Impossible de charger le prestataire</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="relative h-40 bg-gray-200 dark:bg-gray-800 w-full rounded-2xl">
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          <MaterialIcon icon="image" className="text-4xl" />
        </div>
      </div>
      <div className="flex flex-col gap-6 pt-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{provider.name}</h2>
          <p className="text-primary font-medium capitalize">{provider.type}</p>
        </div>

        {provider.specialties && provider.specialties.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {provider.specialties.map((s) => (
              <span
                key={s}
                className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300"
              >
                {s}
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          {provider.phone && (
            <a
              href={`tel:${provider.phone}`}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-[#102216] font-bold py-3 rounded-xl hover:opacity-90 transition-opacity"
            >
              <MaterialIcon icon="call" />
              Appeler
            </a>
          )}
          <button
            type="button"
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${provider.name} ${provider.address} ${provider.city}`)}`, '_blank')}
            className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-white font-bold py-3 rounded-xl hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          >
            <MaterialIcon icon="directions" />
            Y aller
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {(provider.address || provider.city) && (
            <div className="flex items-start gap-4">
              <MaterialIcon icon="location_on" className="text-gray-400 mt-1" />
              <div>
                <p className="font-bold text-gray-900 dark:text-white">Adresse</p>
                <p className="text-gray-500 dark:text-gray-400">
                  {provider.address}<br />
                  {provider.postalCode ? `${provider.postalCode} ` : ''}{provider.city}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-4 p-3 bg-primary/5 rounded-xl border border-primary/10">
            <MaterialIcon icon="verified" className="text-primary mt-1" />
            <div>
              <p className="font-bold text-gray-900 dark:text-white">Tiers Payant</p>
              <p className="text-primary font-semibold text-sm">
                Accepté par tous nos prestataires partenaires
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderDetailContent;
